<template>

    <div class="patent-card flex flex-row gap-4 rounded border border-transparent bg-white p-4 hover:border-eureka-500 hover:bg-grey-10"
         :class="{ '!border-solid !border-grey-40': withBorder }">
        <div class="mt-0.5 shrink-0 text-black">
            {{ order }}
        </div>
        <div class="flex w-full flex-col gap-3">
            <div class="flex cursor-pointer items-center justify-between  gap-2">
                <div class="flex flex-wrap items-center gap-2">
                    <div class="grow">
                        <slot name="preTitle"></slot>
                        <a :href="solutionLink" @click="clickTitleJump"
                           class="cursor-pointer text-base font-semibold text-black hover:text-eureka-600 hover:underline"
                           :title="getContentInnerText(showTitle)" target="_blank" v-html="showTitle"></a>
                        <a class="ml-1 rounded bg-grey-20 px-2 text-xs text-grey-450 h-6 items-center inline-flex"
                           target="_blank" v-if="solution.DOI" :href="doiHref">
                            {{ solution.DOI }}
                        </a>
                        <a class="ml-1 rounded bg-grey-20 px-2 text-xs text-grey-450 h-6 items-center inline-flex"
                           target="_blank" v-if="solution.PN" :href="solutionLink">
                            {{ solution.PN }}
                        </a>
                        <LegalStatus :legal_status="solution.SIMPLE_LEGAL_STATUS" class="ml-2" />
                    </div>

                </div>
                <slot name="customActions" class="shrink-0"></slot>

            </div>

            <div class="line-clamp-3 text-sm text-grey-90" :title="getContentInnerText(solution.CONTENT)">
                <div v-if="showSolutionAiTag"
                     class="solution-card-content__tag inline-block text-grey-450 text-xs py-[2px] px-2 mr-2">
                    {{ t('solutionCard.ai_summary') }}
                </div>
                <span v-html="solution.CONTENT"></span>
            </div>

            <slot name="customContent"></slot>

            <ImageRows v-if="solution.PDF_IMAGE_COUNT" :solution-profile="solution" />

            <SolutionCardFooter :footerField="footerField" :profile="solution" />
        </div>
    </div>

</template>

<script setup lang="ts">
import { EContentType, SOLUTION_TYPE, type ISolution } from './types'
import SolutionCardFooter from './SolutionCardFooter.vue'
import ImageRows from './ImageRows.vue'
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { Site, SLD } from '@patsnap-utils/biz';
import { LegalStatus } from '@eureka/legal-status'
import { getContentInnerText } from '@eureka/utils/get-content-inner-text'
import { cn, tw, en, jp } from './locales';

const { t } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
})
const props = defineProps<{
    solution: ISolution
    order: number
    withBorder: boolean
    footerField?: string
}>();

const emits = defineEmits(['clickTitle'])

const showTitle = computed(() => {
    return props.solution.TITLE || t('noTitle')
})

const showSolutionAiTag = computed(() => {
    return props.solution.CONTENT_TYPE === EContentType.TECHNICAL_TITLE
})
const doiHref = computed(() => {
    return `https://doi.org/${props.solution.DOI}`
})

const solutionLink = computed(() => {
    if (props.solution.SOLUTION_TYPE === SOLUTION_TYPE.PATENT) { return `${new Site({ sld: SLD.EUREKA }).href}/view/#/fullText'figures/?patentId=${props.solution.SOLUTION_ID}` }
    if (props.solution.SOLUTION_TYPE === SOLUTION_TYPE.PAPER) { return `${new Site({ sld: SLD.EUREKA }).href}/literature/#/?paperId=${props.solution.SOLUTION_ID}` }
    return props.solution?.url
})

function clickTitleJump() {
    emits('clickTitle', props.solution)
}

</script>

<style lang="scss" scoped>

.solution-card-content__tag {
    position: relative;
    border-radius: 16px;
    background: linear-gradient(270deg, #18EC81 0%, #0DACBA 28.1%, #0A3DFF 66.22%);
    z-index: 1;
}

.solution-card-content__tag::before {
    content: "";
    position: absolute;
    left: 1px;
    right: 1px;
    top: 1px;
    bottom: 1px;
    border-radius: 16px;
    background-color: white;
    z-index: -1;
}
</style>
